// jwtRefresh.js
import throttle from 'lodash/throttle';
import {
  CONFIG_PRIORITY_PRESENCE_USER,
  CONFIG_PRIORITY_PRESENCE_CLIENT,
} from '../resource/configPriority.js';
import { redux } from '../resource/debug.js';
import { TOKEN_EXPIRED_THRESHOLD } from '../RemoteConfigKeys.js';
import * as MessageTypes from '../serviceWorker/AppMessageTypes.js';

const log = redux.extend('jwtRefresh');
const CHECK_INTERVAL = 10 * 1000; // 10 sec // TODO: remote config
const MAX_REFRESH_AB_TOKEN_COUNT = 3; // TODO: remote config

let expiredThreshold;
let isRefreshingAccessToken = false;
let fetchAccessTokenPromise;
let checkTokenExp;
let abTokenExp;
let isRefreshingAbToken = false;
let refreshAbTokenCount = 0;

const jwtRefresh = store => next => async action => {
  // check the token when action is a function
  if (typeof action === 'function') {
    // if action with _shouldBypass, just next action
    if (action._shouldBypass) return next(action);

    if (!checkTokenExp) checkTokenExp = throttleCheckTokenExp(store);
    checkTokenExp();
  }

  return next(action);
};

const throttleCheckTokenExp = store =>
  throttle(async () => {
    const getMeData = (await import('../selector/getMeData.js')).default;
    const getAbTestingData = (await import('../selector/getAbTestingData.js'))
      .default;
    const state = store.getState();

    if (!expiredThreshold) {
      const getRemoteConfigData = (
        await import('../selector/getRemoteConfigData.js')
      ).default;

      expiredThreshold = +getRemoteConfigData(state, TOKEN_EXPIRED_THRESHOLD);
    }

    const abPriority = getMeData(state, 'id')
      ? CONFIG_PRIORITY_PRESENCE_USER
      : CONFIG_PRIORITY_PRESENCE_CLIENT;

    // get abTokenExp
    if (!abTokenExp)
      abTokenExp = getAbTestingData(state, undefined, 'exp', abPriority);

    if (abTokenExp) {
      if (isRefreshingAbToken) {
        const newAbTokenExp = getAbTestingData(
          state,
          undefined,
          'exp',
          abPriority
        );
        refreshAbTokenCount += 1;

        if (newAbTokenExp > abTokenExp) {
          abTokenExp = newAbTokenExp;
          isRefreshingAbToken = false;
          refreshAbTokenCount = 0;
        }

        log('refreshing ab token', {
          refreshAbTokenCount,
          abTokenExp,
          newAbTokenExp,
        });
      }

      // send message to service worker to get new ab token when
      // 1. the ab token is expired
      // 2. did not get the new token exp after waiting for 3 times checkTokenExp
      if (
        typeof abTokenExp === 'number' &&
        abTokenExp * 1000 - Date.now() < expiredThreshold &&
        (!isRefreshingAbToken ||
          refreshAbTokenCount > MAX_REFRESH_AB_TOKEN_COUNT)
      ) {
        log('get the new ab token', abTokenExp, refreshAbTokenCount);
        const { sendMessageToSW } = await import('../serviceWorker/helpers.js');
        const userId = getMeData(state, 'id');
        const clientId = getMeData(state, 'clientId');

        const event = {
          type: MessageTypes.GET_AB_TEST_TOKEN,
          payload: userId ? { userId } : { clientId },
        };

        sendMessageToSW(event);
        isRefreshingAbToken = true;
        refreshAbTokenCount = 1;
      }
    }

    // access token
    const token = getMeData(state, 'token');
    if (token) {
      // get the current exp from the access token
      const decode = (await import('jwt-decode')).jwtDecode;
      const decoded = decode(token);
      const currentExp = decoded.exp;
      const expTimeDiff = currentExp * 1000 - Date.now();

      if (
        expTimeDiff < expiredThreshold &&
        expTimeDiff > 0 &&
        !isRefreshingAccessToken
      ) {
        log('get the new access token', currentExp);
        isRefreshingAccessToken = true;
        const { default: fetchAccessToken, viaTypes } = await import(
          '../action/fetchAccessToken.js'
        );

        try {
          log('fetchAccessToken');
          const fetchAccessTokenAction = fetchAccessToken({
            via: viaTypes.JWT_REFRESH,
          });
          fetchAccessTokenAction._shouldBypass = true; // hack: the action should bypass
          fetchAccessTokenPromise = store.dispatch(fetchAccessTokenAction);
          await fetchAccessTokenPromise;
          isRefreshingAccessToken = false;
          log('fetchAccessToken done');
        } catch (e) {
          fetchAccessTokenPromise = null;
          isRefreshingAccessToken = false;
          log('fetchAccessToken error', e);
        }
      }
    }
  }, CHECK_INTERVAL);

export default jwtRefresh;
